import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Group as GroupIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getPaginatedUsers, bulkAssignClientsToTherapist } from '../../../services/api';

function AssignClientsModal({ open, therapist, onClose, onSuccess, token }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open && therapist) {
      loadUnassignedClients();
      setSelectedClientIds([]);
      setError('');
    }
  }, [open, therapist, page, rowsPerPage, searchQuery]);

  const loadUnassignedClients = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        role: 'client',
        sortBy: 'name',
        sortOrder: 'asc'
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      const data = await getPaginatedUsers(params, token);

      // Filter to only unassigned clients
      const unassignedClients = (data.users || []).filter(user => !user.assignedTherapist);
      setClients(unassignedClients);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedClientIds(clients.map(c => c._id));
    } else {
      setSelectedClientIds([]);
    }
  };

  const handleSelectOne = (clientId) => {
    setSelectedClientIds(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleAssign = async () => {
    if (selectedClientIds.length === 0) {
      setError('Please select at least one client');
      return;
    }

    // Check capacity
    const currentCount = therapist.therapistInfo?.assignedClientCount || 0;
    const maxCapacity = therapist.therapistInfo?.maxClientCapacity || 20;
    const availableSlots = maxCapacity - currentCount;

    if (selectedClientIds.length > availableSlots) {
      setError(`Therapist only has ${availableSlots} available slots. Selected ${selectedClientIds.length} clients.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      await bulkAssignClientsToTherapist(selectedClientIds, therapist._id, token);
      onSuccess();
    } catch (err) {
      console.error('Error assigning clients:', err);
      setError(err.message || 'Failed to assign clients');
      setLoading(false);
    }
  };

  if (!therapist) return null;

  const currentCount = therapist.therapistInfo?.assignedClientCount || 0;
  const maxCapacity = therapist.therapistInfo?.maxClientCapacity || 20;
  const availableSlots = maxCapacity - currentCount;
  const wouldExceedCapacity = selectedClientIds.length > availableSlots;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          Assign Clients to {therapist.name}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Therapist Info Card */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Current Capacity</Typography>
              <Typography variant="h6">
                {currentCount} / {maxCapacity} clients
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Available Slots</Typography>
              <Typography variant="h6" color={availableSlots > 0 ? 'success.main' : 'error.main'}>
                {availableSlots}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Selected</Typography>
              <Typography variant="h6" color={wouldExceedCapacity ? 'error.main' : 'primary.main'}>
                {selectedClientIds.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {wouldExceedCapacity && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Warning: Selected {selectedClientIds.length} clients exceeds available capacity of {availableSlots} slots.
          </Alert>
        )}

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          label="Search clients"
          placeholder="Name or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Clients Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedClientIds.length > 0 && selectedClientIds.length < clients.length}
                    checked={clients.length > 0 && selectedClientIds.length === clients.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchQuery ? 'No unassigned clients found matching search' : 'No unassigned clients available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow
                    key={client._id}
                    hover
                    onClick={() => handleSelectOne(client._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedClientIds.includes(client._id)} />
                    </TableCell>
                    <TableCell>{client.name}</TableCell>
                    <TableCell>{client.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={client.status}
                        size="small"
                        color={client.status === 'Active' ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={loading || selectedClientIds.length === 0 || wouldExceedCapacity}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? 'Assigning...' : `Assign ${selectedClientIds.length} Client${selectedClientIds.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignClientsModal;
